import { type ComponentProps, type ReactElement, type ReactNode, createContext } from 'react'
import { Select as BaseSelect } from '@base-ui/react/select'
import { twMerge } from 'tailwind-merge'
import { useFieldRequired } from '../field/nested/context'
import { useMaxOptionWidth } from './use-max-option-width'
import { SelectTrigger } from './nested/trigger'
import { SelectPortal } from './nested/portal'
import { SelectPositioner } from './nested/positioner'
import { SelectPopup } from './nested/popup'
import { SelectItem } from './nested/item'
import { SelectScrollUpArrow } from './nested/scroll-up-arrow'
import { SelectScrollDownArrow } from './nested/scroll-down-arrow'
import { SelectContent } from './nested/content'
import type { PartProps } from '../../lib/part-props'

export interface SelectItemType {
  label: string
  value: string | number | null | Record<string, any>
  icon?: ReactElement
  /** A second line under the label in the list. */
  description?: ReactNode
  onClick?: () => void
}

export const SelectItemsContext = createContext<Array<SelectItemType> | undefined>(undefined)
export const SelectMinWidthContext = createContext<string | undefined>(undefined)

interface SelectProps extends PartProps<ComponentProps<typeof BaseSelect.Root>> {
  className?: string
  children: ReactNode
  items?: Array<SelectItemType>
  minWidth?: string
  autoWidth?: boolean
}

function Select({ className, children, items, minWidth, autoWidth = true, required, ...props }: SelectProps) {
  const calculatedMinWidth = useMaxOptionWidth(items, autoWidth && !minWidth)
  const finalMinWidth = minWidth || calculatedMinWidth

  return (
    <SelectItemsContext.Provider value={items}>
      <SelectMinWidthContext.Provider value={finalMinWidth}>
        <BaseSelect.Root required={useFieldRequired(required)} {...props}>
          <div data-slot="select" className={twMerge('flex flex-col gap-2', className)}>
            {children}
          </div>
        </BaseSelect.Root>
      </SelectMinWidthContext.Provider>
    </SelectItemsContext.Provider>
  )
}

Select.Trigger = SelectTrigger
Select.Portal = SelectPortal
Select.Positioner = SelectPositioner
Select.Popup = SelectPopup
Select.Item = SelectItem
Select.ScrollUpArrow = SelectScrollUpArrow
Select.ScrollDownArrow = SelectScrollDownArrow
Select.Value = BaseSelect.Value
Select.Icon = BaseSelect.Icon
Select.Content = SelectContent

export { Select, useMaxOptionWidth }
